import { useCallback, useMemo, useState } from 'react';

import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { noop } from 'ui-components/default-props/noop';

import type { PaymentMethodDetails } from '../../../financial-services-ui/types/fsui';
import { useYupFormValidation } from '../../../validation/use-yup-form-validation';
import type { ValidationError } from '../../../validation/validation-error';
import { messages as rootMessages } from '../messages';
import type { Address, WwexFormData } from '../types';
import { BILLING_COMPANY_NAME_FIELD, BILLING_FULL_NAME_FIELD, buildBillingAddressSchema } from '../wwex-form-validation-schema';

import { BillingAddressForm } from './billing-address-form';
import { messages } from './edit-billing-address-view.messages';
import { SelectedCardDisplay } from './selected-card-display';
import { WwexClearAllLink } from './wwex-clear-all-link';
import { WwexSubViewFooter } from './wwex-sub-view-footer';
import { WwexSubViewHeader } from './wwex-sub-view-header';
import style from './edit-billing-address-view.module.less';
import modalStyle from '../wwex-registration-modal.module.less';

const EMPTY_BILLING_ADDRESS: Address = { line1: '', city: '', state: '', postalCode: '', countryCode: 'US' };

type EditBillingAddressViewProps = {
  headerId: string;
  formData: Partial<WwexFormData>;
  selectedPaymentMethod?: PaymentMethodDetails;
  isDisabled: boolean;
  onSave: (data: Partial<WwexFormData>) => void;
  onBack: () => void;
  onCancel: () => void;
};

export const EditBillingAddressView = ({
  headerId,
  formData,
  selectedPaymentMethod,
  isDisabled,
  onSave,
  onBack,
  onCancel,
}: EditBillingAddressViewProps) => {
  const [draft, setDraft] = useState({
    fullName: formData.billingFullName ?? '',
    companyName: formData.billingCompanyName ?? '',
    address: formData.billingAddress ?? EMPTY_BILLING_ADDRESS,
  });
  const [showAllErrors, setShowAllErrors] = useState(false);

  const schema = useMemo(() => buildBillingAddressSchema(), []);
  const validationForm = useMemo(
    () => ({
      [BILLING_FULL_NAME_FIELD]: draft.fullName,
      [BILLING_COMPANY_NAME_FIELD]: draft.companyName,
      ...draft.address,
    }),
    [draft],
  );
  const { errors, isValid } = useYupFormValidation({ form: validationForm, schema });

  const handleFieldChange = useCallback((field: string, value: string | Address) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleClearAll = useCallback(() => setDraft({ fullName: '', companyName: '', address: EMPTY_BILLING_ADDRESS }), []);

  const handleSave = useCallback(() => {
    if (!isValid) {
      setShowAllErrors(true);
      return;
    }

    onSave({
      ...formData,
      billingFullName: draft.fullName,
      billingCompanyName: draft.companyName,
      billingAddress: draft.address,
    });
  }, [draft, formData, isValid, onSave]);

  return (
    <>
      <ModalHeader className={modalStyle.modalHeader} closeModal={onCancel} id={headerId}>
        <WwexSubViewHeader isDisabled={isDisabled} onBack={onBack} titleMessage={rootMessages.editBillingAddressTitle} />
      </ModalHeader>

      <ModalBody className={modalStyle.modalBody}>
        <FlexColumn gap={Gap.Space16}>
          {selectedPaymentMethod && <SelectedCardDisplay paymentMethod={selectedPaymentMethod} />}

          <FlexRow className={style.sectionHeaderRow} gap={Gap.Space8}>
            <Text textType={TextType.H4} value={messages.sectionTitle} />
            <WwexClearAllLink isDisabled={isDisabled} onClick={handleClearAll} />
          </FlexRow>

          <BillingAddressForm
            data={draft}
            errors={errors as Record<string, ValidationError>}
            isDisabled={isDisabled}
            onBlur={noop}
            onChange={handleFieldChange}
            showAllErrors={showAllErrors}
          />
        </FlexColumn>
      </ModalBody>

      <ModalFooter>
        <WwexSubViewFooter isDisabled={isDisabled} isSaveDisabled={!isValid} onBack={onBack} onCancel={onCancel} onSave={handleSave} />
      </ModalFooter>
    </>
  );
};
